import React, { useEffect, useState, useRef } from "react";
import Slider from "@mui/material/Slider";
import { Grid, Button, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import useFormStore from "../state/useFormStore";
import styled from "styled-components";
import { apiService } from "../utils/api";
import { config } from '../config';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import withAuthAndData from './withAuthAndData';
import { formatCurrency } from "../utils/utils";

declare global {
  interface Window {
    PresterLoanCalculator: any;
  }
}

const Container = styled.div`
  width: 90%;
  max-width: 600px;
  margin: auto;
  text-align: center;
  background: white;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const Subtitle = styled.h2`
  font-size: 14px;
  font-weight: 500;
  color: #666;
  margin-bottom: 8px;
`;

const Disclaimer = styled.p`
  font-size: 12px;
  color: #666;
  margin-top: 16px;
  padding: 8px;
  background-color: #f5f5f5;
  border-radius: 8px;
  line-height: 1.4;
`;

const Section = styled.div`
  margin-bottom: 16px;
`;

const ValueDisplay = styled.p`
  font-size: 28px;
  font-weight: 600;
  color: #000;
  margin: 8px 0;
`;

const Label = styled.h3`
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
`;

const SliderContainer = styled.div`
  width: 100%;
  position: relative;
  padding: 0 10px;
  margin: 20px 0;
`;

const SliderLayout = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 10px;
`;

const ReferenceValue = styled.span`
  font-size: 12px;
  font-weight: bold;
  color: #666;
  width: 60px;
  text-align: center;
`;

const SliderContent = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  height: 40px;
`;

const SliderWrapper = styled.div`
  flex: 1;
  margin: 0 20px;
  display: flex;
  align-items: center;
  height: 100%;
  
  .MuiSlider-markLabel {
    transform: translateY(20px);
  }
`;

const ControlButton = styled(IconButton)`
  width: 40px !important;
  height: 40px !important;
  background-color: #000 !important;
  color: #fff !important;
  position: relative !important;
  z-index: 1 !important;

  &:hover {
    background-color: #333 !important;
  }
  &:disabled {
    background-color: #ccc !important;
    color: #fff !important;
  }
`;

interface Offer {
  id: string;
  maxAmount: number;
  minAmount: number;
  installments: number;
  rate: number;
  firstDueDate: string;
  maxInstallmentAmount: number;
  minInstallmentAmount: number;
}

const OfertaReal: React.FC = () => {
  const navigate = useNavigate();
  const hash = useFormStore((state) => state.hash) || localStorage.getItem("hash");
  const { 
    requestUid, 
    selectedOfferData, 
    setSelectedOfferData,
    first_name,
    offers: zustandOffers,
    setOffer
  } = useFormStore((state) => ({
    requestUid: state.requestUid,
    selectedOfferData: state.selectedOfferData,
    setSelectedOfferData: state.setSelectedOfferData,
    first_name: state.first_name,
    offers: state.offers,
    setOffer: state.setOffer
  }));

  const [calculator, setCalculator] = useState<any>(null);
  const [selectedOffer, setSelectedOffer] = useState<Offer | null>(null);
  const [localOffers, setLocalOffers] = useState<Offer[]>([]);
  const [monto, setMonto] = useState<number>(0);
  const [cuotas, setCuotas] = useState<number>(0);
  const [rate, setRate] = useState<number>(0);
  const [firstDueDate, setFirstDueDate] = useState<string>("");
  const [cuotaMensual, setCuotaMensual] = useState<number>(0);
  const [totalPagar, setTotalPagar] = useState<number>(0);
  const [tea, setTea] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [offersLoading, setOffersLoading] = useState<boolean>(true);
  const [calculatorLoading, setCalculatorLoading] = useState<boolean>(true);
  const scriptLoaded = useRef<boolean>(false);
  const calculatorInstance = useRef<any>(null);
  const [calculatorResult, setCalculatorResult] = useState<any>(null);

  useEffect(() => {
    const loadCalculatorScript = () => {
      if (scriptLoaded.current || document.getElementById('prester-loan-calculator')) {
        if (window.PresterLoanCalculator && !calculatorInstance.current) {
          calculatorInstance.current = new window.PresterLoanCalculator();
          setCalculatorLoading(false);
        }
        return;
      }

      if (!config.sdk.loanCalculator) {
        console.error('La URL del SDK del calculador de préstamos no está definida');
        setCalculatorLoading(false);
        return;
      }

      const script = document.createElement("script");
      script.id = 'prester-loan-calculator';
      script.src = config.sdk.loanCalculator;

      const handleScriptLoad = () => {
        if (window.PresterLoanCalculator) {
          calculatorInstance.current = new window.PresterLoanCalculator();
          scriptLoaded.current = true;
          setCalculatorLoading(false);
        }
      };

      script.onload = handleScriptLoad;
      script.onerror = () => setCalculatorLoading(false);

      document.body.appendChild(script);
    };

    loadCalculatorScript();
  }, []);

  useEffect(() => {
    const fetchOffers = async () => {
      if (!requestUid) return;
      
      try {
        setOffersLoading(true);
        const response = await apiService.requests.getOffers(requestUid);
        if (response.data?.data?.finalOffers) {
          const fetchedOffers = response.data.data.finalOffers;
          setOffer(fetchedOffers); // Update Zustand store
          setLocalOffers(fetchedOffers);
          
          // Initialize with max installments offer
          const maxInstallments = Math.max(...fetchedOffers.map((o: Offer) => o.installments));
          const initialOffer = fetchedOffers.find((o: Offer) => o.installments === maxInstallments);
          
          if (initialOffer) {
            setSelectedOffer(initialOffer);
            setCuotas(initialOffer.installments);
            setMonto(initialOffer.maxAmount);
            setRate(initialOffer.rate);
            setFirstDueDate(initialOffer.firstDueDate);
          }
        }
      } catch (error) {
        console.error("Error fetching offers:", error);
      } finally {
        setOffersLoading(false);
      }
    };

    const initializeOffers = () => {
      if (!requestUid) return;
      
      if (!zustandOffers?.length) {
        // If no offers in Zustand, fetch them
        fetchOffers();
      } else {
        // Use existing offers from Zustand
        const maxInstallments = Math.max(...zustandOffers.map(o => o.installments));
        const initialOffer = zustandOffers.find(o => o.installments === maxInstallments);
        
        if (initialOffer) {
          setSelectedOffer(initialOffer);
          setCuotas(initialOffer.installments);
          setMonto(initialOffer.maxAmount);
          setRate(initialOffer.rate);
          setFirstDueDate(initialOffer.firstDueDate);
          setLocalOffers(zustandOffers);
        }
        
        setOffersLoading(false);
      }
    };

    initializeOffers();
  }, [requestUid, zustandOffers, setOffer]);

  useEffect(() => {
    const currentCalculator = calculatorInstance.current;

    if (!calculatorLoading && !offersLoading && currentCalculator && selectedOffer && monto > 0 && cuotas > 0) {
      console.log('CALCULAR: Intentando calcular cuota con:', {
        monto,
        cuotas,
        rate: selectedOffer.rate,
        calculatorReady: !!currentCalculator,
        offerId: selectedOffer.id
      });
      try {
        if (typeof currentCalculator.setup !== 'function' || typeof currentCalculator.calculate !== 'function') {
           console.error("CALCULAR: La instancia de la calculadora no tiene los métodos esperados (setup/calculate).");
           setCuotaMensual(0);
           return;
        }

        currentCalculator.setup({
          amount: monto,
          installments: cuotas,
          interest: selectedOffer.rate * 100,
        });

        const result = currentCalculator.calculate();
        setCalculatorResult(result);
        console.log('CALCULAR: Resultado del cálculo:', result);

        if (result && result.installments && result.installments.length > 0) {
          const cuota = result.installments[0].amount;
          if (typeof cuota === 'number' && cuota > 0) {
            console.log(`CALCULAR: Cuota calculada: ${cuota}`);
            setCuotaMensual(cuota);
            setTotalPagar(cuota * cuotas);
            setTea(result.loan.tea);
          } else {
            console.warn('CALCULAR: Cálculo resultó en cuota inválida:', cuota);
            setCuotaMensual(0);
          }
        } else {
          console.warn('CALCULAR: El resultado del cálculo no contiene cuotas válidas o está vacío.');
          setCuotaMensual(0);
        }
      } catch (error) {
        console.error('CALCULAR: Error durante el cálculo de la cuota:', error);
        setCuotaMensual(0);
      }
    } else {
      console.log('CALCULAR: Saltando cálculo. Requisitos no cumplidos o en carga:', {
        calculatorLoading,
        offersLoading,
        calculatorReady: !!currentCalculator,
        offerSelected: !!selectedOffer,
        montoValid: monto > 0,
        cuotasValid: cuotas > 0
      });
    }
  }, [monto, cuotas, selectedOffer, calculatorLoading, offersLoading]);

  const handleMontoChange = (_event: Event, value: number | number[]) => {
    setMonto(value as number);
  };

  const handleMontoIncrement = () => {
    const step = 5000;
    const newValue = Math.min(monto + step, selectedOffer?.maxAmount || 500000);
    setMonto(newValue);
  };

  const handleMontoDecrement = () => {
    const step = 5000;
    const newValue = Math.max(monto - step, selectedOffer?.minAmount || 10000);
    setMonto(newValue);
  };

  const handleCuotasChange = (_event: Event, value: number | number[]) => {
    const cuotasSeleccionadas = value as number;
    const offer = localOffers.find(
      (offer: Offer) => offer.installments === cuotasSeleccionadas
    );
    if (offer) {
      setSelectedOffer(offer);
      setCuotas(cuotasSeleccionadas);
      setMonto(offer.maxAmount);
      setRate(offer.rate);
      setFirstDueDate(offer.firstDueDate);
    }
  };

  const handleCuotasIncrement = () => {
    const availableInstallments = localOffers.map((o: Offer) => o.installments).sort((a: number, b: number) => a - b);
    const currentIndex = availableInstallments.indexOf(cuotas);

    console.error(currentIndex);
    console.error(availableInstallments);
    
    if (currentIndex < availableInstallments.length - 1) {
      const newValue = availableInstallments[currentIndex + 1];
      const offer = localOffers.find((o: Offer) => o.installments === newValue);
      if (offer) {
        setSelectedOffer(offer);
        setCuotas(newValue);
        setMonto(offer.maxAmount);
        setRate(offer.rate);
        setFirstDueDate(offer.firstDueDate);
      }
    }
  };

  const handleCuotasDecrement = () => {
    const availableInstallments = localOffers.map((o: Offer) => o.installments).sort((a: number, b: number) => a - b);
    const currentIndex = availableInstallments.indexOf(cuotas);
    
    if (currentIndex > 0) {
      const newValue = availableInstallments[currentIndex - 1];
      const offer = localOffers.find((o: Offer) => o.installments === newValue);
      if (offer) {
        setSelectedOffer(offer);
        setCuotas(newValue);
        setMonto(offer.maxAmount);
        setRate(offer.rate);
        setFirstDueDate(offer.firstDueDate);
      }
    }
  };

  const handleConfirmar = async () => {
    if (!selectedOffer || !requestUid) {
      alert("Error: Oferta o solicitud no definidas");
      return;
    }

    setLoading(true);

    try {
      const response = await apiService.requests.selectOffer(requestUid, {
        offerId: selectedOffer.id,
        selectedAmount: monto,
        selectedInstallmentAmount: cuotaMensual
      });

      if (response.data && response.data.data && response.data.data.selectedOffer) {
        setSelectedOfferData({
          offerId: selectedOffer.id,
          selectedAmount: monto,
          selectedInstallmentAmount: cuotaMensual,
          selectedInstallments: cuotas,
        });

        // Guardar en localStorage todos los datos relevantes para el resumen
        const resumenObj = {
          offerId: selectedOffer.id,
          selectedAmount: monto,
          selectedInstallmentAmount: cuotaMensual,
          selectedInstallments: cuotas,
          cbu: localStorage.getItem('cbu') || '', // O el valor real si lo tienes
          firstDueDate: firstDueDate,
          rate: rate,
          tea: tea,
          calculatorResult,
          offerRaw: selectedOffer,
        };
        localStorage.setItem('selectedOfferResumen', JSON.stringify(resumenObj));
        localStorage.setItem('selectedOfferId', selectedOffer.id);

        navigate(`/${hash}/cbu`);
      } else {
        throw new Error("Error al procesar la respuesta de la API");
      }
    } catch (error) {
      console.error("Error al enviar la oferta:", error);
      alert("Hubo un error al seleccionar la oferta.");
    } finally {
      setLoading(false);
    }
  };

  const formatDueDate = (dateStr: string) => {
    if (!dateStr) return "";
    
    // Primero dividimos la fecha en sus componentes
    const [year, month, day] = dateStr.split("-");
    
    // Si el día es 10, lo cambiamos por 5
    const adjustedDay = day === "10" ? "05" : day;
    
    return `${adjustedDay}/${month}/${year}`;
  };

  const minCuotas = localOffers.length > 0 ? Math.min(...localOffers.map((o) => o.installments)) : 6;
  const maxCuotas = localOffers.length > 0 ? Math.max(...localOffers.map((o) => o.installments)) : 12;
  const minMonto = selectedOffer?.minAmount || 10000;
  const maxMonto = selectedOffer?.maxAmount || 500000;

  return (
    <Container>
      {(!calculatorInstance.current || offersLoading) ? (
        <p>Cargando oferta...</p>
      ) : localOffers.length > 0 ? (
        <>
          <Subtitle>
            Refinamos nuestra oferta basada en tus datos, vuelve a revisarla y confirma tu aceptación:
          </Subtitle>
          <Section>
            <Label>Monto</Label>
            <ValueDisplay>{formatCurrency(monto)}</ValueDisplay>
            
            <SliderContainer>
              <SliderContent>
                <ControlButton 
                  onClick={handleMontoDecrement}
                  disabled={monto <= minMonto}
                >
                  <RemoveIcon />
                </ControlButton>
                
                <SliderWrapper>
                  <Slider
                    value={monto}
                    onChange={handleMontoChange}
                    min={minMonto}
                    max={maxMonto}
                    step={5000}
                    marks={[
                      {
                        value: minMonto,
                        label: `${formatCurrency(minMonto)}`,
                      },
                      {
                        value: maxMonto,
                        label: `${formatCurrency(maxMonto)}`,
                      },
                    ]}
                    sx={{
                      color: "#000000",
                      "& .MuiSlider-thumb": {
                        backgroundColor: "#000000",
                        width: 24,
                        height: 24,
                      },
                      "& .MuiSlider-track": {
                        backgroundColor: "#000000",
                        height: 7,
                      },
                      "& .MuiSlider-rail": {
                        backgroundColor: "#95d0dd",
                        height: 7,
                      },
                      "& .MuiSlider-mark": {
                        display: "none",
                      },
                      "& .MuiSlider-markLabel": {
                        display: "none",
                      }
                    }}
                  />
                </SliderWrapper>
                
                <ControlButton 
                  onClick={handleMontoIncrement}
                  disabled={monto >= maxMonto}
                >
                  <AddIcon />
                </ControlButton>
              </SliderContent>

              <SliderLayout>
                <ReferenceValue>
                  {formatCurrency(minMonto)}
                </ReferenceValue>
                <ReferenceValue>
                  {formatCurrency(maxMonto)}
                </ReferenceValue>
              </SliderLayout>
            </SliderContainer>
          </Section>

          <Section>
            <Label>Plazo</Label>
            <ValueDisplay>{cuotas} {cuotas === 1 ? 'mes' : 'meses'}</ValueDisplay>
            
            <SliderContainer>
              <SliderContent>
                <ControlButton 
                  onClick={handleCuotasDecrement}
                  disabled={cuotas <= minCuotas}
                >
                  <RemoveIcon />
                </ControlButton>
                
                <SliderWrapper>
                  <Slider
                    value={cuotas}
                    onChange={handleCuotasChange}
                    min={minCuotas}
                    max={maxCuotas}
                    step={null}
                    marks={localOffers.map(o => ({ value: o.installments, label: '' }))}
                    sx={{
                      color: "#000000",
                      "& .MuiSlider-thumb": {
                        backgroundColor: "#000000",
                        width: 24,
                        height: 24,
                      },
                      "& .MuiSlider-track": {
                        backgroundColor: "#000000",
                        height: 7,
                      },
                      "& .MuiSlider-rail": {
                        backgroundColor: "#95d0dd",
                        height: 7,
                      },
                      "& .MuiSlider-mark": {
                        display: "none",
                      },
                      "& .MuiSlider-markLabel": {
                        display: "none",
                      }
                    }}
                  />
                </SliderWrapper>
                
                <ControlButton 
                  onClick={handleCuotasIncrement}
                  disabled={cuotas >= maxCuotas}
                >
                  <AddIcon />
                </ControlButton>
              </SliderContent>

              <SliderLayout>
                <ReferenceValue>
                  {localOffers.length > 0 ? `${minCuotas} meses` : ''}
                </ReferenceValue>
                <ReferenceValue>
                  {localOffers.length > 0 ? `${maxCuotas} meses` : ''}
                </ReferenceValue>
              </SliderLayout>
            </SliderContainer>
          </Section>

          <Section>
            <Label>Cuota mensual</Label>
            <ValueDisplay>
              {formatCurrency(cuotaMensual, { decimals: 2 })}
            </ValueDisplay>
          </Section>

          <div style={{ fontSize: 12, color: "grey", marginBottom: 16 }}>
            <p>Vencimiento: {formatDueDate(String(firstDueDate))}</p>
            <p>TNA: {(rate*100).toFixed(0)}%</p>
          </div>

          <Button
            variant="contained"
            onClick={handleConfirmar}
            disabled={loading}
            style={{
              backgroundColor: "#FFF200",
              color: "black",
              fontWeight: 600,
              boxShadow: "none",
              borderRadius: 50,
              padding: "12px 40px",
              textTransform: "none",
              fontSize: 16,
            }}
          >
            {loading ? "Cargando..." : "Continuar"}
          </Button>

          <Disclaimer>
            Oferta sujeta a calificación crediticia final
          </Disclaimer>
        </>
      ) : (
        <p>No se encontraron ofertas disponibles en este momento.</p>
      )}
    </Container>
  );
};

export default withAuthAndData(OfertaReal);